import { Row } from 'antd'
import lodashPickBy from 'lodash/pickBy'
import { useMemo, useState } from 'react'
import { useSelector, shallowEqual } from 'react-redux'
import { useTable } from 'react-table'
import styled from 'styled-components'

import { getFormattedDate } from '../../clients/tools'
import { AuthLayout } from '../../components/AuthLayout'
import { Icon } from '../../components/Icon'
import { NoneMessage } from '../../components/NoneMessage'
import { PageBody } from '../../components/PageBody'
import { PageLabel } from '../../components/PageLabel'
import { PaginationContainer } from '../../components/PaginationContainer'
import { SearchInput } from '../../components/SearchInput'
import { Select } from '../../components/Select'
import { Spacer } from '../../components/Spacer'
import { Table, TableCell, TableHeadCell, TableRow } from '../../components/Table'
import { useDriverCheckinHistory } from '../../hooks/useCheckinHistory'
import { useModal } from '../../hooks/useModal'
import type { CheckIn, UpdateCheckinData } from '../../models/client/checkins'
import { selectSiteDirectory } from '../../store/sites'
import { selectCompanyId, selectUser } from '../../store/user'
import { isEqual } from '../../util/isEqual'

const Container = styled.div`
  display: flex;
  flex: 1 1 auto;
  min-height: 0;
  flex-flow: column nowrap;
`

const StyledPageBody = styled(PageBody)`
  overflow: auto;
  min-height: 0px;
  flex: 1 1 auto;
`
const SelectContainer = styled.div`
  width: 300px;
  float: left;
`
const StyledSearchInput = styled(SearchInput)`
  max-width: 500px;
  float: left;
`

const HeaderRow = styled(TableRow)`
  position: sticky;
  top: 0;
  z-index: 500;
`

const StyledTable = styled(Table)`
  min-height: 500px;
`

const Cell = styled(TableCell)`
  height: 100px;
`

const RowContainer = styled.div`
  min-width: 0px;
`

const Overflow = styled.div`
  overflow: auto;
  min-height: 0px;
  flex: 1 1 auto;
  display: flex;
  flex-flow: column;
`

const NoneContainer = styled.div`
  display: flex;
  flex: 1 0 auto;
  height: 400px;
  width: 100%;
`

export function CheckinHistory(): JSX.Element {
  const companyId = useSelector(selectCompanyId, shallowEqual)
  const user = useSelector(selectUser, isEqual)

  const [activeCheckin, setCurrentActiveCheckin] = useState(null)

  let sites = useSelector(selectSiteDirectory, isEqual)
  sites = lodashPickBy(sites, (v) => {
    return v.companyId === companyId
  })

  let cardData: UpdateCheckinData = {}

  const {
    isLoading,
    siteId,
    setSiteId,
    checkins,
    pageSize,
    page,
    totalItems,
    setPage,
    setSearchTerm,
    refetch,
    updateCheckinData,
  } = useDriverCheckinHistory('')

  const columns = useMemo(
    () => [
      {
        Header: 'Date',
        accessor: 'createdAt',
      },
      {
        Header: 'Status',
        accessor: 'status.key',
      },
      {
        Header: 'Driver Name',
        accessor: 'driverName',
      },
      {
        Header: 'Phone Number',
        accessor: 'phoneNumber',
      },
      {
        Header: 'Carrier Name',
        accessor: 'carrierName',
      },
      {
        Header: 'Customer Name',
        accessor: 'customerName',
      },
      {
        Header: 'BOL/PO Number',
        accessor: 'loadNumber',
      },
      {
        Header: 'Chassis Number',
        accessor: 'chassisNumber',
      },
      {
        Header: 'Container Number',
        accessor: 'assetNumber',
      },
      {
        Header: 'Files',
        accessor: (row) => {
          return row.meta?.files
        },
        Cell: (cell) => {
          const { row, value } = cell
          const rowId = row.original.id as string

          return (
            <ul key={`${rowId}-files`}>
              {value &&
                value.map((item, index: number) => (
                  <li key={`${rowId}-files-file${index}`}>
                    <a
                      key={`${rowId}-files-file${index}-link`}
                      target="_blank"
                      href={item.url}
                      rel="noreferrer"
                    >
                      {item.tags[0].replace('-', ' ').toUpperCase()}
                    </a>
                  </li>
                ))}
            </ul>
          )
        },
      },
      {
        Header: 'Action',
        accessor: (row) => {
          return row
        },
        Cell: (cell) => {
          const { row } = cell
          const rowId = row.original.id as string

          return (
            <div>
              {user?.permissions.includes('users') && (
                <Icon
                  iconKey="CircleExOutlined"
                  size={5}
                  color="danger"
                  onClick={() => {
                    onDeleteClick(row.original)
                  }}
                  key={`${rowId}-checkin-action-delete`}
                />
              )}
            </div>
          )
        },
      },
    ],
    []
  )

  const data = useMemo(
    () =>
      checkins.map((checkin: CheckIn) => {
        return {
          ...checkin,
          driverName: `${checkin.firstName} ${checkin.lastName}`,
          createdAt: getFormattedDate(checkin.createdAt),
        }
      }),
    [checkins]
  )

  const { rows, headers, prepareRow } = useTable({
    data,
    columns,
  })

  const COL_SIZE = `repeat(${headers.length}, 170px)`

  const handleFilterSearch = async (): Promise<void> => {
    await refetch()
  }

  const resetCardData = (): void => {
    cardData = {}
  }

  const [Modal, triggerModal] = useModal({
    onCancel: () => {
      resetCardData()
    },
    onConfirm: () => {
      cardData.archivedAt = new Date().toISOString()
      updateCheckinData(activeCheckin.id, cardData)
      resetCardData()
    },
  })

  const onDeleteClick = (checkinData): void => {
    setCurrentActiveCheckin(checkinData)
    triggerModal(true)
  }

  return (
    <AuthLayout>
      <StyledPageBody>
        <Container>
          <PageLabel
            title="Check-In History"
            description="Search all checkin-in activities by BOL/PO or container number"
          />
          <Spacer vertical size={4} />
          <RowContainer>
            <SelectContainer>
              <Select
                className="check-in-site-select"
                placeholder="Select a Site"
                options={Object.keys(sites).map((val) => {
                  return {
                    label: sites[val].name,
                    value: sites[val].id,
                  }
                })}
                value={siteId}
                onChange={(nextValue) => {
                  setSiteId(nextValue)
                }}
              />
            </SelectContainer>
            <StyledSearchInput
              label=""
              placeholder="Enter BOL/PO or Container Number or Phone."
              onSearchValue={(nextValue) => {
                if (nextValue.length > 2 || nextValue === '') {
                  setSearchTerm(nextValue)
                }
              }}
              onSearch={handleFilterSearch}
              onEnterPress={handleFilterSearch}
            />
          </RowContainer>

          <Spacer vertical size={4} />
          <PaginationContainer
            totalItems={totalItems}
            currentPage={page}
            pageSize={pageSize}
            disabled={isLoading}
            onPageSelect={(index) => {
              setPage(index)
            }}
          >
            <StyledTable>
              <Overflow>
                <HeaderRow columnSize={COL_SIZE} defaultColumnSize="0px" rowSize="50px">
                  {headers.map((header, cellIndex) => (
                    <TableHeadCell key={`header-${cellIndex}`} {...header.getHeaderProps()}>
                      {header.render('Header')}
                    </TableHeadCell>
                  ))}
                </HeaderRow>

                {rows.map((row) => {
                  prepareRow(row)
                  const rowId = row.original.id as string

                  return (
                    <RowContainer key={`${row.id}`} {...row.getRowProps()}>
                      <TableRow columnSize={COL_SIZE} onClick={() => {}} defaultColumnSize="0px">
                        {row.cells.map((cell, cellIndex: number) => (
                          <Cell key={`${rowId}-${cellIndex}`} {...cell.getCellProps()}>
                            {cell.render('Cell')}
                          </Cell>
                        ))}
                      </TableRow>
                    </RowContainer>
                  )
                })}
              </Overflow>
              {rows.length === 0 ? (
                <NoneContainer>
                  <NoneMessage message="No Data" />
                </NoneContainer>
              ) : (
                <div />
              )}
            </StyledTable>
          </PaginationContainer>
        </Container>
        <Modal confirmLabel="Delete" cancelLabel="Cancel" widthPx={860} key="checkin-action-modal">
          <Row key="checkin-action-modal-row">
            <h3>Please confirm removing this entry?</h3>
          </Row>
        </Modal>
      </StyledPageBody>
    </AuthLayout>
  )
}
